import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { BaseApiRequest, ParsedDate, parseIsoDate } from '@api/Trinium/types';

interface DocumentRawFields {
	id: number;
	csnId: number;
	csnCustRefNo?: string;
	canUndoScanDocument: boolean;
	custId: string;
	custName: string;
	typeCode: string;
	type: string;
	holdsDrvPay: boolean;
	holdsInv: boolean;
	printsWithInv: boolean;
	isFulFilled: boolean;
	isPOD: boolean;
	isScanned: boolean;
	modDateTime?: string | null;
	usrId: string;
	usrName: string;
	cntrId: string;
}

export type Document = GridValidRowModel &
	Omit<DocumentRawFields, 'modDateTime'> & {
		modDateTime?: ParsedDate;
		isRequired: boolean;
	};

export function transformDocumentResponse(result: DocumentRawFields | undefined): Document {
	return {
		...result,
		modDateTime: parseIsoDate(result?.modDateTime),
		isRequired: result?.holdsDrvPay || result?.holdsInv || result?.printsWithInv || result?.isPOD
	} as Document;
}

export function transformDocumentsResponse(result: DocumentRawFields[] | undefined): Document[] {
	return result
		? sortDocuments(
				result.map((item) => {
					return transformDocumentResponse(item);
				})
			)
		: [];
}

/** Method for sorting the Documents for the Documents grid */
export function sortDocuments(documents: Document[]) {
	return documents.sort((a, b) => {
		// sort by Document id
		return a.id - b.id;
	});
}

export interface FetchDocumentRequest {
	/** Document Identifier */
	id: number;
}

// TODO: update as needed once response from API has been updated
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FetchDocumentResponse {}

export interface PostDocumentRequest extends BaseApiRequest {
	fileData?: File;
	docId?: number;
	docTypeId: string;
	csnId: number;
}

export const maxDocumentFileSize = 5 * 1024 * 1024; // 5 MB (binary)

export const acceptedDocumentFileTypes = {
	'application/pdf': ['.pdf'],
	'text/plain': ['.txt'],
	'image/jpeg': ['.jpg', '.jpeg'],
	'image/png': ['.png'],
	'image/tiff': ['.tif', '.tiff'],
	'application/msword': ['.doc'],
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
	'application/vnd.ms-excel': ['.xls'],
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
	'text/csv': ['.csv'],
	'text/markdown': ['.md'],
	'message/rfc822': ['.eml'],
	'application/vnd.ms-outlook': ['.msg']
};

export interface PutDocumentsRequest extends BaseApiRequest {
	actionType: PutDocumentsAction;
	consignmentId?: number;
}

export interface PutDocumentRequest extends BaseApiRequest {
	/** Document Identifier */
	id: number;
	actionType: PutDocumentAction;
	consignmentId: number;
}

interface ChangeDocumentTypeAction {
	action: 'changeDocumentType';
	data: {
		docType: string;
	};
}

interface ApproveDocumentsAction {
	action: 'approveDocuments';
	data: {
		docIds: number[];
	};
}

interface DeleteDocumentsAction {
	action: 'deleteDocuments';
	data: {
		docIds: number[];
	};
}

interface EmailDocumentsAction {
	action: 'emailDocuments';
	data: {
		addr: string;
		subject: string;
		body: string;
		csnId: number;
		docIds: number[];
	};
}

interface UnscanDocumentsAction {
	action: 'unscan';
	data: {
		csnId: number;
		docIds: number[];
	};
}

export interface DocumentType {
	id: string;
	name: string;
}

export type PutDocumentAction = ChangeDocumentTypeAction;

export type PutDocumentsAction = ApproveDocumentsAction | DeleteDocumentsAction | EmailDocumentsAction | UnscanDocumentsAction;
